import React, { useRef, useState } from "react";

import CheckOutlinedIcon from "@mui/icons-material/CheckOutlined";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import PersonAddOutlinedIcon from "@mui/icons-material/PersonAddOutlined";
import { IconButton, Menu, MenuItem, MenuList, Stack, Typography } from "@mui/material";
import { FolderIcon, VisibilityLock } from "assets/material-custom-icons";
import { RoleComponentGuard } from "components/guards";
import { useDealParamsHooks } from "hooks";
import { IDocumentMetaDetail } from "store/types";

import { RoleName } from "../../../constants";
import { useDocumentHook } from "../Hooks/use-document-hook";

export type DocumentActionsHook = typeof useDocumentHook;

const menuPaperSx = {
  borderRadius: "8px",
  boxShadow:
    "0px 3px 3px -2px rgba(38,54,70,0.16), 0px 3px 4px 0px rgba(38,54,70,0.1), 0px 1px 8px 0px rgba(38,54,70,0.08)",
  minWidth: 200,
  py: "8px"
};

const menuItemSx = {
  display: "flex",
  alignItems: "center",
  gap: "8px",
  px: "12px",
  py: "8px",
  fontSize: 16,
  color: "#071f35",
  minHeight: 40,
  "& .MuiSvgIcon-root": { fontSize: 20, color: "#496682", flexShrink: 0 },
  "&:hover": { backgroundColor: "#ecf1f8" }
};

export interface Props {
  row: IDocumentMetaDetail;
  refetchHandler: () => Promise<void>;
  useHook?: DocumentActionsHook;
}

export const DocumentActions: React.FC<Props> = ({ row, refetchHandler, useHook = useDocumentHook }) => {
  const menuTriggerRef = useRef<HTMLButtonElement>(null);
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const { dealId } = useDealParamsHooks();

  const openMenu = (e: React.MouseEvent<HTMLElement>) => {
    e.stopPropagation();
    setMenuAnchor(e.currentTarget);
  };
  const closeMenu = () => setMenuAnchor(null);
  const handleAction = (fn?: () => void) => {
    closeMenu();
    fn?.();
  };

  const {
    handleShare,
    handleRename,
    handleDeleteDocument,
    handleAddTask,
    handleChangePrivacy,
    handleConvertToGeneral
  } = useHook({
    document: {
      id: row.id,
      title: row.title,
      deal_id: dealId,
      is_private: row.is_private
    },
    refetchHandler
  });

  const menuOptions = [
    {
      title: "Share",
      icon: <PersonAddOutlinedIcon />,
      onClick: () => handleAction(() => handleShare?.()),
      hidden: row.is_private || !row.deal_id,
      disabled: !handleShare
    },
    {
      title: "Rename",
      icon: <EditOutlinedIcon />,
      onClick: () => handleRename?.(menuTriggerRef, closeMenu),
      hidden: false,
      disabled: !handleRename
    },
    {
      title: "Convert to General",
      icon: <FolderIcon sx={{ fill: "#5D7782" }} />,
      onClick: () => handleAction(() => handleConvertToGeneral?.()),
      hidden: true,
      disabled: !handleConvertToGeneral
    },
    {
      title: "Add Task",
      icon: <CheckOutlinedIcon />,
      onClick: () => handleAction(() => handleAddTask?.()),
      hidden: false,
      disabled: !handleAddTask
    },
    {
      title: row.is_private ? "Set to Public" : "Set to Private",
      icon: <VisibilityLock sx={{ "& path": { fill: "#496682 !important" } }} />,
      onClick: () => handleAction(() => handleChangePrivacy?.(!row.is_private)),
      hidden: !row.deal_id,
      disabled: !handleChangePrivacy
    },
    {
      title: "Delete",
      icon: <DeleteOutlineOutlinedIcon />,
      onClick: () => handleAction(() => handleDeleteDocument?.(row.id)),
      hidden: false,
      disabled: !handleDeleteDocument
    }
  ];

  return (
    <RoleComponentGuard availableRoles={[RoleName.Admin, RoleName.Analyst]}>
      <>
        <Stack
          onClick={(e) => e.stopPropagation()}
          direction="row"
          className="row-hover-actions"
          justifyContent="flex-end"
          sx={{ opacity: 0, transition: "opacity 0.1s" }}
        >
          <IconButton
            ref={menuTriggerRef}
            size="sm"
            onClick={openMenu}
            aria-expanded={Boolean(menuAnchor)}
            sx={{ p: "4px", color: "#071f35", ...(menuAnchor && { opacity: 0.7 }) }}
          >
            <MoreVertIcon sx={{ fontSize: 20 }} />
          </IconButton>
        </Stack>

        <Menu
          anchorEl={menuAnchor}
          open={Boolean(menuAnchor)}
          onClose={closeMenu}
          onClick={(e: React.MouseEvent<HTMLElement>) => e.stopPropagation()}
          anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
          transformOrigin={{ vertical: "top", horizontal: "left" }}
          slotProps={{ paper: { sx: menuPaperSx } }}
          MenuListProps={{ sx: { padding: 0 } }}
        >
          <MenuList disablePadding>
            {menuOptions
              .filter((option) => !option.hidden)
              .map((option) => (
                <MenuItem key={option.title} sx={menuItemSx} onClick={option.onClick} disabled={option.disabled}>
                  {option.icon}
                  <Typography sx={{ fontSize: 16, color: "#071f35" }}>{option.title}</Typography>
                </MenuItem>
              ))}
          </MenuList>
        </Menu>
      </>
    </RoleComponentGuard>
  );
};
